// @services/stubs/StubDocumentAssistantRetrievalContextPreviewClient.ts
import type { TaskEitherTs } from "@ps-bridge/interop";
import type { DocumentAssistantRetrievalContextPreviewClient } from "@services/clients/DocumentAssistantRetrievalContextPreviewClient";
import {
    contextPreviewErrorFromRefusal,
    contextPreviewLocalError,
    decodeContextPreviewRefusal,
    decodeContextPreviewResult,
    type ContextPreviewApiDecodeResult,
    type ContextPreviewError,
    type ContextPreviewJsonPayload,
    type ContextPreviewRefusalCode,
    type ContextPreviewRequest,
    type ContextPreviewResult,
} from "@services/clients/DocumentAssistantRetrievalContextPreviewApi";
import { taskEitherChain, taskEitherLeft, taskEitherOf, taskEitherTryCatch } from "@utils/effects/branded";
import * as Option from "@utils/Option";
import { ok, type Ok } from "@ps-bridge/ok";

export type StubContextPreviewMode =
    | "success"
    | "readDenied"
    | "invalidRequest"
    | "missingReviewedWorkspace"
    | "sendAttemptRefused"
    | "networkError"
    | "responseDecodeError";

export const STUB_CONTEXT_PREVIEW_MODE_KEY = "stub:context-preview:mode";
export const STUB_CONTEXT_PREVIEW_DELAY_KEY = "stub:context-preview:delayMs";

const STUB_TRACE = {
    traceId: "stub-context-preview-trace",
    requestId: "stub-context-preview-request",
};
const STUB_PRIOR_LISTING_ID = "018f3ad5-39d4-7bd8-9f79-5bb5f6f34d10";
const SARAH_SIGNATURE_SOURCE = "email:best-bagels:sarah-signature";
const SARAH_TRANSCRIPT_SOURCE = "transcript:best-bagels:intake";
const STEVE_PRIOR_SOURCE = "email:prior-listing:steve-offer";
const jsonNull = (): ContextPreviewJsonPayload => JSON.parse("null") as ContextPreviewJsonPayload;

const browserStorage = () =>
    typeof window === "undefined" ? Option.none<Storage>() : Option.some(window.localStorage);

const readMode = (): StubContextPreviewMode => {
    const value = Option.fold(browserStorage(), () => "", (storage) => storage.getItem(STUB_CONTEXT_PREVIEW_MODE_KEY) ?? "");
    switch (value) {
        case "readDenied":
        case "invalidRequest":
        case "missingReviewedWorkspace":
        case "sendAttemptRefused":
        case "networkError":
        case "responseDecodeError":
        case "success":
            return value;
        default:
            return "success";
    }
};

const readDelayMs = (): number => {
    const raw = Option.fold(browserStorage(), () => "0", (storage) => storage.getItem(STUB_CONTEXT_PREVIEW_DELAY_KEY) ?? "0");
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const delay = (millis: number): Promise<Ok> =>
    new Promise((resolve) => {
        globalThis.setTimeout(() => resolve(ok), millis);
    });

const decodeOrError = <T>(
    result: ContextPreviewApiDecodeResult<T>,
): TaskEitherTs<ContextPreviewError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value) as TaskEitherTs<ContextPreviewError, T>;
    }

    return taskEitherLeft(contextPreviewLocalError("responseDecodeError", result.message));
};

const buildRefusal = (
    error: ContextPreviewRefusalCode,
    message: string,
): ContextPreviewError => {
    const decoded = decodeContextPreviewRefusal({
        error,
        message,
        trace: STUB_TRACE,
    });

    if (decoded.type === "ok") {
        return contextPreviewErrorFromRefusal(decoded.value);
    }

    return contextPreviewLocalError("responseDecodeError", decoded.message);
};

const modeError = (mode: Exclude<StubContextPreviewMode, "success">): ContextPreviewError => {
    switch (mode) {
        case "readDenied":
            return buildRefusal(
                "readDenied",
                "Retrieval preview read permission denied for this listing workspace.",
            );
        case "invalidRequest":
            return buildRefusal(
                "invalidRequest",
                "Preview request is malformed; listing id, reviewed workspace id, purpose, and prompt are required.",
            );
        case "missingReviewedWorkspace":
            return buildRefusal(
                "readDenied",
                "Reviewed retrieval workspace required for this listing before context preview can load.",
            );
        case "sendAttemptRefused":
            return buildRefusal(
                "sendAttemptRefused",
                "Context preview refuses send attempts; use an explicit governed send workflow after user authorization.",
            );
        case "networkError":
            return contextPreviewLocalError("networkError", "The retrieval context preview service is unavailable.");
        case "responseDecodeError":
            return contextPreviewLocalError("responseDecodeError", "Unable to decode retrieval context preview response.");
    }
};

const evidenceGroup = (
    listingIds: readonly string[],
    sourceRefs: readonly string[],
    bundleCount: number,
    claimCount: number,
) => ({
    bundleCount,
    claimCount,
    listingIds,
    sourceRefs,
    redactionSafeCount: bundleCount,
    requiresReviewCount: 0,
});

const stubSuccessPayload = (listingId: string, request: ContextPreviewRequest) => ({
    workspaceId: request.workspaceId,
    workspaceVersion: request.workspaceVersion,
    listingId,
    purpose: request.purpose,
    promptEcho: request.prompt,
    sources: [
        {
            sourceId: SARAH_SIGNATURE_SOURCE,
            revision: 4,
            granularity: "EmailSignature",
            audit: "listing.workspace:Best Bagels:seller representative signature",
        },
        {
            sourceId: SARAH_TRANSCRIPT_SOURCE,
            revision: 2,
            granularity: "TranscriptSpan",
            audit: "listing.workspace:Best Bagels:intake transcript",
        },
        {
            sourceId: STEVE_PRIOR_SOURCE,
            revision: 1,
            granularity: "EmailThread",
            audit: "prior listing context: Steve buyer application",
        },
    ],
    citations: [
        {
            sourceId: SARAH_SIGNATURE_SOURCE,
            sourceLabel: "Sarah email signature",
            evidenceLabel: "current",
            priorListingId: jsonNull(),
            authority: "manualAssertion",
            confidence: 0.97,
            authorityGaps: [],
            rationale: "Signature block on Best Bagels email identifies Sarah as seller representative and includes her phone number.",
        },
        {
            sourceId: SARAH_TRANSCRIPT_SOURCE,
            sourceLabel: "Best Bagels intake transcript",
            evidenceLabel: "current",
            priorListingId: jsonNull(),
            authority: "sourceDerived",
            confidence: 0.88,
            authorityGaps: ["missingManualAssertion"],
            rationale: "Transcript links Sarah to the Best Bagels diligence meeting but still needs manual confirmation.",
        },
        {
            sourceId: STEVE_PRIOR_SOURCE,
            sourceLabel: "Steve prior buyer application email",
            evidenceLabel: "priorListing",
            priorListingId: STUB_PRIOR_LISTING_ID,
            authority: "unresolved",
            confidence: 0.55,
            authorityGaps: ["priorListingOnly"],
            rationale: "Steve has prior buyer activity that may inform relationship context but is not current Best Bagels evidence.",
        },
    ],
    evidenceSummary: {
        current: evidenceGroup([listingId], [SARAH_SIGNATURE_SOURCE, SARAH_TRANSCRIPT_SOURCE], 2, 2),
        priorListings: evidenceGroup([STUB_PRIOR_LISTING_ID], [STEVE_PRIOR_SOURCE], 1, 1),
        draftOnly: evidenceGroup([], [], 0, 0),
        authorityGaps: ["missingManualAssertion", "priorListingOnly"],
    },
    confirmedInferences: [
        {
            claimId: "best-bagels-sarah-seller-rep",
            listingId,
            kind: "partyRole",
            value: "Sarah is the seller representative for Best Bagels.",
            disposition: "confirmed",
            provenance: {
                sourceRef: SARAH_SIGNATURE_SOURCE,
                span: {
                    startMs: 0,
                    endMs: 0,
                },
                speaker: "Sarah",
                observedAt: "2026-05-12T17:30:00Z",
            },
        },
    ],
    unresolvedQuestions: [
        {
            question: "Confirm whether Sarah's mobile number is still the best phone number for Best Bagels.",
            listingId,
            evidenceSource: SARAH_TRANSCRIPT_SOURCE,
            provenance: {
                sourceRef: SARAH_TRANSCRIPT_SOURCE,
                span: {
                    startMs: 116000,
                    endMs: 139000,
                },
                speaker: "broker",
                observedAt: "2026-05-12T17:30:00Z",
            },
        },
        {
            question: "Decide whether Steve's prior buyer activity is useful for this Best Bagels prompt.",
            listingId,
            evidenceSource: STEVE_PRIOR_SOURCE,
            provenance: {
                sourceRef: STEVE_PRIOR_SOURCE,
                span: {
                    startMs: 0,
                    endMs: 0,
                },
                speaker: "Steve",
                observedAt: "2026-05-01T14:00:00Z",
            },
        },
    ],
    cacheState: {
        freshness: "Fresh",
        workspaceVersion: request.workspaceVersion,
        profileCacheKey: `preview:${request.workspaceId}:${request.workspaceVersion}`,
        sourceRevisions: [
            {
                sourceId: SARAH_SIGNATURE_SOURCE,
                revision: 4,
            },
            {
                sourceId: SARAH_TRANSCRIPT_SOURCE,
                revision: 2,
            },
            {
                sourceId: STEVE_PRIOR_SOURCE,
                revision: 1,
            },
        ],
        deletionNoticeCount: 0,
    },
    readiness: {
        draftAllowed: true,
        sendGateType: jsonNull(),
        sendAuthorized: false,
        noSendReady: true,
    },
    authoritySummary: {
        manualAssertionCount: 1,
        unresolvedCitationCount: 1,
        priorListingCitationCount: 1,
        authorityGaps: ["missingManualAssertion", "priorListingOnly"],
    },
    readOnly: true,
    profileMode: "defaultPreviewProfile",
    trace: STUB_TRACE,
});

export class StubDocumentAssistantRetrievalContextPreviewClient implements DocumentAssistantRetrievalContextPreviewClient {
    preview(
        listingId: string,
        request: ContextPreviewRequest,
    ): TaskEitherTs<ContextPreviewError, ContextPreviewResult> {
        const delayTask = taskEitherTryCatch(
            () => delay(readDelayMs()),
            () => contextPreviewLocalError("networkError", "The retrieval context preview stub was interrupted."),
        );

        return taskEitherChain(delayTask, () => {
            if (request.sendRequestAttempted) {
                return taskEitherLeft(modeError("sendAttemptRefused"));
            }

            const mode = readMode();
            if (mode !== "success") {
                return taskEitherLeft(modeError(mode));
            }

            return decodeOrError(decodeContextPreviewResult(stubSuccessPayload(listingId, request)));
        });
    }
}
